<template>
  <v-card
    class="ma-0 px-1 app-form-page"
    :class="{
      'app-form-page--fixed': !isEmbedded && !isMobile,
      'app-form-page--mobile': isMobile && !isEmbedded,
      'app-form-page--embedded': isEmbedded,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header (no tabs / no documents) -->
    <div v-if="isMobile && !isEmbedded" class="amf-header">
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            v-if="id"
            type="button"
            class="amf-header__btn amf-header__btn--create"
            :aria-label="$t('detail')"
            @click="goTo('detail')"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            v-if="!formType"
            type="button"
            class="amf-header__btn"
            :aria-label="$t('detailList')"
            @click="goTo('detailList')"
          >
            <v-icon size="20" color="#37474F">mdi-format-list-numbered</v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">{{ $t("detail") }}</h1>
        <button
          v-if="!formType"
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
        <span v-else class="amf-header__btn amf-header__btn--back" style="visibility: hidden" />
      </div>
    </div>

    <!-- Desktop / embedded toolbar -->
    <v-app-bar
      v-else
      flat
      class="my-0 app-form-toolbar"
      :class="
        isMobile
          ? 'mb-2 py-1 px-2 app-form-toolbar--mobile'
          : 'mb-0 py-0 px-2'
      "
      dense
      :height="!isEmbedded && !isMobile ? 40 : undefined"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ $t("detail") }}
      </v-toolbar-title>
      <v-checkbox
        v-model="status"
        class="mt-0 mx-2"
        dense
        hide-details
        :label="$t('status') + '  :  ' + (status ? $t('active') : $t('deactive'))"
        value="active"
      />
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip v-if="id" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo('detail')"
            >
              <v-icon color="teal"> mdi-plus-box-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("detail") }}</span>
        </v-tooltip>
        <v-tooltip v-if="!formType" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo('detailList')"
            >
              <v-icon color="primary"> mdi-format-list-numbered </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("detailList") }}</span>
        </v-tooltip>
        <v-tooltip v-if="!formType" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary"> mdi-arrow-left </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("back") }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>

    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />

    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form"
      lazy-validation
    >
      <!-- Mobile summary -->
      <div v-if="isMobile && !isEmbedded" class="amf-summary" dir="rtl">
        <div class="amf-summary__status">
          <div
            class="amf-summary__chip"
            :class="status ? 'amf-summary__chip--green' : 'amf-summary__chip--pink'"
          >
            {{ $t("status") }} : {{ status ? $t("active") : $t("deactive") }}
          </div>
        </div>

        <button type="button" class="amf-summary__row" @click="nameSheet = true">
          <span class="amf-summary__label">{{ $t("name") }}</span>
          <span
            class="amf-summary__value"
            :class="{ 'amf-summary__value--empty': !name }"
          >{{ name || "—" }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <button
          type="button"
          class="amf-summary__row"
          :disabled="!!id"
          @click="!id && openMobileAutocomplete('refGroup')"
        >
          <span class="amf-summary__label">{{ $t("detailGroup") }}</span>
          <span
            class="amf-summary__value"
            :class="{ 'amf-summary__value--empty': !group }"
          >{{ mobileGroupText }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <button
          type="button"
          class="amf-summary__row"
          @click="openMobileAutocomplete('refSystem')"
        >
          <span class="amf-summary__label">{{ $t("systemLink") }}</span>
          <span
            class="amf-summary__value"
            :class="{ 'amf-summary__value--empty': !system }"
          >{{ mobileSystemText }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <button
          type="button"
          class="amf-summary__row amf-summary__row--tall"
          @click="otherSheet = true"
        >
          <span class="amf-summary__label">{{ $t("otherInputs") }}</span>
          <span class="amf-summary__value amf-summary__value--muted">{{
            $t("other-inputs-hint")
          }}</span>
          <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <div class="amf-sr-only" aria-hidden="true">
          <v-text-field v-model="name" :rules="[(v) => !!v || $t('is-required')]" />
          <autocomplete
            ref="refGroup"
            v-model="group"
            :rules="[(v) => !!v || $t('is-required')]"
            :title="$t('detailGroup')"
            type="detailGroup"
            :value="group"
            :disabled="id ? true : false"
          />
          <autocomplete
            ref="refSystem"
            v-model="system"
            :title="$t('systemLink')"
            :value="system"
            :data="JSON.stringify({ type: 'detail' })"
            type="system"
          />
        </div>
      </div>

      <!-- Desktop / embedded body -->
      <div v-if="!isMobile || isEmbedded" class="app-form-body">
        <v-container fluid class="app-form-container app-form-container--cards pa-3">
          <v-row class="app-form-card-grid ma-0" dense>
            <v-col cols="12" md="7" class="pa-2">
              <div class="app-form-card">
                <div class="app-form-card__header">
                  <div class="app-form-card__icon" aria-hidden="true">
                    <v-icon color="primary" size="22">mdi-file-tree-outline</v-icon>
                  </div>
                  <div class="app-form-card__heading">
                    <div class="app-form-card__title">
                      {{ $t("public-information") }}
                    </div>
                    <div class="app-form-card__subtitle">
                      {{ $t("detail") }}
                    </div>
                  </div>
                </div>
                <div class="app-form-card__body">
                  <v-row class="app-form-fields ma-0">
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="name"
                        :label="$t('name')"
                        dense
                        :rules="[(v) => !!v || $t('is-required')]"
                        outlined
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <autocomplete
                        v-model="group"
                        :rules="[(v) => !!v || $t('is-required')]"
                        :title="$t('detailGroup')"
                        type="detailGroup"
                        :value="group"
                        :disabled="id ? true : false"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <autocomplete
                        v-model="system"
                        :title="$t('systemLink')"
                        :value="system"
                        :data="JSON.stringify({ type: 'detail' })"
                        type="system"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="5" class="pa-2">
              <div class="app-form-card">
                <div class="app-form-card__header">
                  <div class="app-form-card__icon" aria-hidden="true">
                    <v-icon color="primary" size="22">mdi-tune-variant</v-icon>
                  </div>
                  <div class="app-form-card__heading">
                    <div class="app-form-card__title">
                      {{ $t("otherInputs") }}
                    </div>
                    <div class="app-form-card__subtitle">
                      {{ $t("other-inputs-hint") }}
                    </div>
                  </div>
                </div>
                <div class="app-form-card__body app-form-card__body--stack">
                  <v-row class="app-form-fields ma-0">
                    <v-col cols="12" sm="6">
                      <tag v-model="tag" :solo="false" :input="tag" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="custom_code"
                        :label="$t('Custom financial ID')"
                        outlined
                        clearable
                        dense
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" class="app-form-fields__grow">
                      <description
                        ref="refContent"
                        v-model="content"
                        class="payment-description"
                        :outlined="true"
                        :rows="4"
                        type="sell"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile sheets -->
      <template v-if="isMobile && !isEmbedded">
        <v-bottom-sheet v-model="nameSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("name") }}</div>
              <button type="button" class="amf-sheet__back" @click="nameSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body">
              <v-text-field
                v-model="name"
                :label="$t('name')"
                dense
                :rules="[(v) => !!v || $t('is-required')]"
                outlined
                clearable
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="nameSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="otherSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("otherInputs") }}</div>
              <button type="button" class="amf-sheet__back" @click="otherSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <v-checkbox
                v-model="status"
                class="mt-0"
                dense
                hide-details
                :label="$t('status') + '  :  ' + (status ? $t('active') : $t('deactive'))"
                value="active"
              />
              <tag v-model="tag" :solo="false" :input="tag" />
              <v-text-field
                v-model="custom_code"
                :label="$t('Custom financial ID')"
                outlined
                clearable
                dense
              />
              <description
                ref="refContent"
                v-model="content"
                class="payment-description"
                :outlined="true"
                :rows="4"
                type="sell"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="otherSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>
      </template>

      <div class="app-form-page__actions">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="create"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="update"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="!formType"
            color="primary"
            outlined
            depressed
            class="app-form-action-btn"
            @click="goTo('detailList')"
          >
            {{ $t("detailList") }}
          </v-btn>
        </div>
      </div>
    </v-form>

    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import General from "../../../../Mixins/general";
import Description from "../../components/general/description/Description";
export default {
  name: "Product",
  components: {
    autocomplete: () => import("../../components/general/patial/autocomplete"),
    Description,
    Tag: () => import("../../components/general/Tag/tag"),
  },
  mixins: [General],
  props: ["formType", "returnObject"],
  data: () => ({
    options: {
      locale: "en-US",
      prefix: "",
      suffix: " ",
      precision: 0,
    },
    render: false,
    isLoading: false,
    alert: false,
    system: null,
    tag: null,
    content: null,
    valid: null,
    message: null,
    alertType: null,
    name: null,
    type: null,
    id: null,
    status: "active",
    group: null,
    loading: false,
    custom_code: null,
    nameSheet: false,
    otherSheet: false,
  }),
  computed: {
    isEmbedded() {
      return !!this.formType;
    },
    mobileGroupText() {
      if (!this.group) return "—";
      return this.group.name || this.group.title || "—";
    },
    mobileSystemText() {
      if (!this.system) return "—";
      return this.system.name || this.system.title || "—";
    },
  },
  watch: {
    isMobile(val) {
      if (this.isEmbedded) return;
      this.lockPageScroll(!!val);
    },
  },
  beforeMount() {
    if (this.$route.params.id != null) {
      this.show();
      this.id = this.$route.params.id;
      this.render = false;
    } else {
      this.render = true;
    }
  },
  mounted() {
    if (this.isMobile && !this.isEmbedded) {
      this.lockPageScroll(true);
    }
  },
  beforeDestroy() {
    this.lockPageScroll(false);
  },
  methods: {
    openMobileAutocomplete(refName) {
      const ref = this.$refs[refName];
      if (!ref || typeof ref.openMobileDialog !== "function") return;
      if (ref.disabled) return;
      ref.openMobileDialog();
    },
    lockPageScroll(lock) {
      const value = lock ? "hidden" : "";
      const overscroll = lock ? "none" : "";
      document.documentElement.style.overflow = value;
      document.documentElement.style.overscrollBehavior = overscroll;
      document.body.style.overflow = value;
      document.body.style.overscrollBehavior = overscroll;
      const main = document.querySelector(".v-main");
      const wrap = document.querySelector(".v-main__wrap");
      if (main) {
        main.style.overflow = value;
        main.style.overscrollBehavior = overscroll;
      }
      if (wrap) {
        wrap.style.overflow = value;
        wrap.style.overscrollBehavior = overscroll;
      }
    },
    create() {
      if (this.$refs.form.validate()) {
        var app = this;
        app.alert = false;
        app.isLoading = true;
        app.$axios
          .post(this.$endpoint + "detail", app.$data)
          .then((res) => {
            app.goTo("detailList");
            if (app.id) {
              // app.goTo('detailList')
            } else {
              app.$emit("input", res.data.data);
              app.snakbarText = app.$t("registered");
              app.snakbarColor = "green";
              app.snakbar = true;
            }
            this.clearForm();
          })
          .catch((err) => {
            this.serverErrorHandeler(err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    show() {
      var app = this;
      app.clearForm();
      app.loading = true;
      app.clearing = true;
      app.showing = true;
      app.$axios
        .get(this.$endpoint + "detail/" + this.$route.params.id + "/edit", {
          params: {
            id: this.$route.params.id,
          },
        })
        .then((res) => {
          res = res.data;
          app.$nextTick(() => {
            app.id = res.id;
            app.name = res.name;
            app.system = res.system;
            app.group = res.group;
            app.content = res.content;
            app.custom_code = res.custom_code;
            if (res.tags) {
              app.tag = res.tags;
            }
            app.render = true;
          });
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    update() {
      var app = this;
      app.isLoading = true;
      app.$axios
        .put(this.$endpoint + "detail/" + this.$route.params.id, this.$data)
        .then((res) => {
          res = res.data;
          app.$emit("close", app.$route);
          app.goTo("detailList");
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => (app.isLoading = false));
    },
    clearForm() {
      this.tag = null;
      this.content = null;
      this.name = null;
      this.system = null;
      this.id = null;
      this.status = "active";
      this.type = null;
      this.group = null;
      this.custom_code = null;
    },
  },
};
</script>

<style scoped></style>
